import Drawer from '@/components/Drawer';
import {
  fetchMyMailboxRequest,
  replyLeadMailRequest,
  sendLeadMailRequest,
} from '@/services/api/email';
import {
  MailMessage,
  MailRecipient,
  MyMailbox,
  RenderedTemplate,
} from '@/typings/models/Email';
import { F } from '@/utils/intl';
import { processAPIResponseNow } from '@/utils/request';
import {
  FileTextOutlined,
  InfoCircleOutlined,
  SendOutlined,
} from '@ant-design/icons';
import {
  Button,
  Form,
  Input,
  message,
  Select,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import styles from './EmailComposer.less';
import EmailTemplateSelector from './EmailTemplateSelector';
import TipTapEditor from './TipTapEditor';

const { Text } = Typography;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ReplyContext {
  message: MailMessage;
  mailbox: string;
  replyAll: boolean;
}

interface EmailComposerProps {
  open: boolean;
  leadId: string;
  /** Prefill for new messages — the lead's customer address. */
  defaultTo?: string | null;
  /** When set, the composer is a reply into this message's Outlook thread. */
  replyContext?: ReplyContext | null;
  onClose: () => void;
  onSent: (sentFromMailbox: string) => void;
}

/**
 * Rich-text composer for lead mail. New messages go out from the resolved
 * send-as mailbox; replies thread into the real Outlook conversation via
 * createReply on the mailbox holding it. The response reports which mailbox
 * the mail actually left from.
 */
const EmailComposer: React.FC<EmailComposerProps> = ({
  open,
  leadId,
  defaultTo,
  replyContext,
  onClose,
  onSent,
}) => {
  const intl = useIntl();
  const [form] = Form.useForm();
  const [html, setHtml] = useState('');
  const [sending, setSending] = useState(false);
  const [templateSelectorOpen, setTemplateSelectorOpen] = useState(false);
  const [myMailbox, setMyMailbox] = useState<MyMailbox | null>(null);

  const isReply = Boolean(replyContext);

  /**
   * Who the reply will reach — mirrors Graph's createReply semantics (the
   * sender, plus everyone on the message for reply-all, minus the mailbox we
   * reply from). The server computes the authoritative list; this shows the
   * user what to expect *before* sending, which the composer previously did
   * not do at all.
   */
  const replyRecipients = useMemo(() => {
    if (!replyContext) return [];
    const self = replyContext.mailbox.toLowerCase();
    const seen = new Map<string, string>();
    const add = (recipient?: MailRecipient) => {
      const address = recipient?.emailAddress?.address;
      if (!address || address.toLowerCase() === self) return;
      if (!seen.has(address.toLowerCase())) {
        seen.set(
          address.toLowerCase(),
          recipient?.emailAddress?.name || address,
        );
      }
    };
    add(replyContext.message.from);
    if (replyContext.replyAll) {
      (replyContext.message.toRecipients || []).forEach(add);
      (replyContext.message.ccRecipients || []).forEach(add);
    }
    if (seen.size === 0) {
      // Replying to a message this mailbox itself sent: the sender is us, so
      // the reply goes back to the original recipients (Graph does the same).
      (replyContext.message.toRecipients || []).forEach(add);
    }
    return Array.from(seen.values());
  }, [replyContext]);

  useEffect(() => {
    if (!open) return;
    setHtml('');
    form.resetFields();
    if (!isReply && defaultTo) {
      form.setFieldsValue({ to: [defaultTo] });
    }
    if (isReply && replyContext) {
      form.setFieldsValue({
        subject: replyContext.message.subject?.startsWith('Re:')
          ? replyContext.message.subject
          : `Re: ${replyContext.message.subject || ''}`,
      });
    }
    fetchMyMailboxRequest().then((Response: any) => {
      processAPIResponseNow(
        Response,
        {
          onSuccess(response: any) {
            setMyMailbox(response);
          },
          // 409 (not connected) just hides the identity row.
          onError() {},
        },
        'data',
      );
    });
  }, [open, isReply, defaultTo, replyContext, form]);

  const handleTemplate = (rendered: RenderedTemplate) => {
    if (!isReply && rendered.subject) {
      form.setFieldsValue({ subject: rendered.subject });
    }
    setHtml(rendered.html);
  };

  const validRecipients = (_rule: unknown, value?: string[]) => {
    if (!value || value.every((address) => EMAIL_RE.test(address))) {
      return Promise.resolve();
    }
    return Promise.reject(
      new Error(
        intl.formatMessage({
          id: 'email.composer.invalid_recipient',
          defaultMessage:
            'One or more addresses are not valid email addresses.',
        }),
      ),
    );
  };

  const handleSend = () => {
    form.validateFields().then((values) => {
      if (!html || html === '<p></p>') {
        message.error(
          intl.formatMessage({ id: 'email.composer.body_required' }),
        );
        return;
      }
      setSending(true);

      const requestPromise =
        isReply && replyContext
          ? replyLeadMailRequest(leadId, replyContext.message.id, {
              mailbox: replyContext.mailbox,
              html,
              reply_all: replyContext.replyAll,
            })
          : sendLeadMailRequest(leadId, {
              subject: values.subject,
              html,
              to: values.to,
              cc: values.cc,
            });

      requestPromise.then((Response: any) => {
        processAPIResponseNow(
          Response,
          {
            onSuccess(response: any) {
              message.success(
                intl.formatMessage(
                  { id: 'email.composer.sent_as' },
                  { mailbox: response.sent_from_mailbox },
                ),
              );
              onSent(response.sent_from_mailbox);
              onClose();
            },
            onError(error: any) {
              message.error(error?.message);
            },
            onEnd() {
              setSending(false);
            },
          },
          'data',
        );
      });
    });
  };

  const recipientsPlaceholder = intl.formatMessage({
    id: 'email.composer.add_recipients',
    defaultMessage: 'Type an address and press Enter',
  });

  return (
    <Drawer
      title={
        isReply ? (
          <F id="email.composer.reply_title" defaultMessage="Reply" />
        ) : (
          <F id="email.composer.new_title" defaultMessage="New email" />
        )
      }
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      width={640}
      footer={
        <div className={styles.footer}>
          <Button
            type="primary"
            icon={<SendOutlined />}
            loading={sending}
            onClick={handleSend}
          >
            <F id="email.action.send" defaultMessage="Send" />
          </Button>
        </div>
      }
    >
      <Form form={form} layout="vertical" className={styles.composerForm}>
        {myMailbox && (
          <div className={styles.metaRow}>
            <Text type="secondary" className={styles.metaLabel}>
              <F id="email.composer.from" defaultMessage="From" />
            </Text>
            <Text className={styles.metaValue}>{myMailbox.send_as}</Text>
          </div>
        )}
        {isReply && replyRecipients.length > 0 && (
          <div className={styles.metaRow}>
            <Text type="secondary" className={styles.metaLabel}>
              <F id="email.composer.to" defaultMessage="To" />
            </Text>
            <span className={styles.metaValue}>
              {replyRecipients.map((label) => (
                <Tag key={label} className={styles.recipientChip}>
                  {label}
                </Tag>
              ))}
            </span>
          </div>
        )}
        {isReply && (
          <div className={styles.metaRow}>
            <Text type="secondary" className={styles.metaLabel}>
              <F id="email.composer.subject" defaultMessage="Subject" />
            </Text>
            <Text className={styles.metaValue} ellipsis>
              {replyContext?.message.subject?.startsWith('Re:')
                ? replyContext.message.subject
                : `Re: ${replyContext?.message.subject || ''}`}
            </Text>
          </div>
        )}
        {!isReply && (
          <>
            <Form.Item
              name="to"
              label={<F id="email.composer.to" defaultMessage="To" />}
              rules={[
                {
                  required: true,
                  type: 'array',
                  min: 1,
                  message: intl.formatMessage({
                    id: 'email.composer.to_required',
                    defaultMessage: 'Add at least one recipient.',
                  }),
                },
                { validator: validRecipients },
              ]}
            >
              <Select
                mode="tags"
                tokenSeparators={[',', ' ']}
                open={false}
                suffixIcon={null}
                placeholder={recipientsPlaceholder}
              />
            </Form.Item>
            <Form.Item
              name="cc"
              label={<F id="email.composer.cc" defaultMessage="Cc" />}
              rules={[{ validator: validRecipients }]}
            >
              <Select
                mode="tags"
                tokenSeparators={[',', ' ']}
                open={false}
                suffixIcon={null}
                placeholder={recipientsPlaceholder}
              />
            </Form.Item>
          </>
        )}
        <Form.Item
          name="subject"
          label={<F id="email.composer.subject" defaultMessage="Subject" />}
          rules={[{ required: !isReply }]}
          hidden={isReply}
        >
          <Input />
        </Form.Item>
        <div className={styles.editorSection}>
          <TipTapEditor value={html} onChange={setHtml} fill />
        </div>
        {isReply && (
          <Text type="secondary" className={styles.quotedHint}>
            <F
              id="email.composer.quoted_hint"
              defaultMessage="The previous conversation is quoted below your reply automatically."
            />
          </Text>
        )}
      </Form>
      <EmailTemplateSelector
        open={templateSelectorOpen}
        leadId={leadId}
        onClose={() => setTemplateSelectorOpen(false)}
        onSelect={handleTemplate}
      />
    </Drawer>
  );
};

export default EmailComposer;
